package ru.it_mozaika.sender.js.application.context

import js.core.jso
import mui.material.AlertColor
import react.*
import react.router.useNavigate
import ru.it_mozaika.components.useSnackbar
import ru.it_mozaika.sender.common.models.EmailCampaignData
import ru.it_mozaika.sender.common.models.EmailCampaignVariant
import ru.it_mozaika.sender.js.application.Http
import ru.it_mozaika.utils.checkResponseError


external interface CampaignContextInterface {
    var campaign: EmailCampaignData
    var update: (EmailCampaignData) -> Unit
    var isSaved: Boolean
    var save: () -> Unit
}

val CampaignContext = createContext<CampaignContextInterface>(jso {
    campaign = newEmailCampaignData("", "")
    update = {}
    isSaved = false
    save = {}
})
external interface CampaignProviderProps : PropsWithChildren {
    var campaign: EmailCampaignData?
}

val CampaignProvider = FC<CampaignProviderProps> { props ->
    val backDrop = useBackdrop()
    val navigate = useNavigate()
    val showNotification = useSnackbar().enqueueSnackbar

    val storeContext = useStore()
    val senderContext = useSender()
    val subscriptionListContext = useSubscriptionLists()

    val (campaign, setCampaign) = useState(newEmailCampaignData(storeContext.currentStoreId.orEmpty(), senderContext.currentSender.id))
    var isSaved by useState(props.campaign != null && !props.campaign?.id.isNullOrBlank() && props.campaign?.id != "new")

    useEffect(props.campaign) {
        setCampaign(props.campaign ?: newEmailCampaignData(storeContext.currentStoreId.orEmpty(), senderContext.currentSender.id))
        isSaved = props.campaign != null && !props.campaign?.id.isNullOrBlank() && props.campaign?.id != "new"
        props.campaign?.let {
            if (storeContext.currentStoreId != it.store) {
                storeContext.setStore(it.store)
            }
            if (senderContext.currentSender.id != it.sender) {
                senderContext.setCurrentId(it.sender)
            }
            when {
                it.subscriptionList.isBlank()
                        && subscriptionListContext.currentIds.isNotEmpty() ->
                    subscriptionListContext.setCurrentIds(emptySet())
                it.subscriptionList.isNotBlank()
                        && (!subscriptionListContext.currentIds.contains(it.subscriptionList)
                        || subscriptionListContext.currentIds.size > 1) ->
                    subscriptionListContext.setCurrentIds(setOf(it.subscriptionList))
            }
        }
    }

    useEffect(storeContext.currentStoreId) {
        if (campaign.store != storeContext.currentStoreId) {
            setCampaign { it.copy(store = storeContext.currentStoreId.orEmpty()) }
        }
    }
    useEffect(senderContext.currentSender) {
        if (campaign.sender != senderContext.currentSender.id) {
            setCampaign { it.copy(sender = senderContext.currentSender.id) }
        }
    }
    useEffect(subscriptionListContext.currentIds) {
        if (subscriptionListContext.currentIds.size > 1) {
            error("Subscription list count in Campaign can't be grater than 1")
        }
        if (campaign.subscriptionList != subscriptionListContext.currentIds.firstOrNull().orEmpty()) {
            setCampaign {
                it.copy(subscriptionList = subscriptionListContext.currentIds.firstOrNull().orEmpty())
            }
        }
    }

    CampaignContext.Provider {
        value = jso {
            this.campaign = campaign
            this.update = {
                setCampaign(it)
                isSaved = false
            }
            this.isSaved = isSaved
            this.save = useMemo(campaign) {save@{
                if (isSaved)
                    return@save

                backDrop.show()
                saveCampaign(campaign, { text, color -> showNotification(text, jso { color?.let { variant = color } }) }) { id ->
                    if (campaign.id != id) {
                        navigate("/campaigns/$id")
                    } else {
                        setCampaign { it.copy(id = id) }
                        isSaved = true
                    }
                    backDrop.hide()
                }
            }}
        }
        +props.children
    }

}

private fun newEmailCampaignData(store: String, sender: String): EmailCampaignData {
    return EmailCampaignData(
        id = "",
        name = "Новая рассылка",
        store = store,
        sender = sender,
        subscriptionList = "",
        variants = listOf(EmailCampaignVariant())
    )
}

@Suppress("DeferredResultUnused")
fun saveCampaign(data: EmailCampaignData, showNotification: (String, AlertColor?) -> Unit, callback: (String) -> Unit) {
    if (data.id.isNotBlank()) {
        Http.post(
            "/campaigns/${data.id}",
            data,
            callback = { response ->
                response.checkResponseError()?.let { error ->
                    showNotification(error, AlertColor.error)
                    return@post
                }
                callback(data.id)
            }
        )
    } else {
        Http.put(
            "/campaigns",
            data
        ) { response ->
            response.checkResponseError()?.let { error ->
                showNotification(error, AlertColor.error)
            } ?: kotlin.run {
                response.headers["ID"]?.let { id ->
                    showNotification("Успешно создано", null)
                    callback(id)
                }
            }
        }
    }
}

fun useCampaign() = useContext(CampaignContext)